/* import __COLOCATED_TEMPLATE__ from './chart-legend.hbs'; */
import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import clsx from 'clsx';

const SECONDARY_STYLE_BACKGROUND_OPACITY = 0.2;

const _hexToRgbaSecondaryBackground = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${SECONDARY_STYLE_BACKGROUND_OPACITY})`;
}

const _normalizeHexToCode = (hex) => {
  return hex.substr(0, 1) === '#' ? hex.slice(1) : hex;
}

const CLASS_NAME = 'SH-c-chart-legend';

class ChartLegendComponent extends Component {
  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  #colorValuesMapCache = createCache(() => {
    const { colors } = this.args;
    return this.args.values.reduce((accum, value, index) => {
      accum[_normalizeHexToCode(colors[index])] = value;

      return accum;
    }, {});
  });

  get colorValuesMap() {
    return getValue(this.#colorValuesMapCache);
  }

  #colorTitlesMapCache = createCache(() => {
    const { colors } = this.args;
    return this.args.titles.reduce((accum, title, index) => {
      accum[_normalizeHexToCode(colors[index])] = title;

      return accum;
    }, {});
  });

  get colorTitlesMap() {
    return getValue(this.#colorTitlesMapCache);
  }

  #secondaryBackgroundColorsCache = createCache(() => {
    const backgroundColorSpecifications = this.args.backgroundColors ?? [];
    return Object.keys(this.colorValuesMap).reduce((accum, hexColor, index) => {
      accum[hexColor] = backgroundColorSpecifications[index] ? 
        `#${backgroundColorSpecifications[index]}`
        : _hexToRgbaSecondaryBackground(hexColor);
      return accum;
    }, {});
  });

  /**
   * Use a standard offset if nothing was specified
   */
  get secondaryBackgroundColors() {
    return getValue(this.#secondaryBackgroundColorsCache);
  }

  #childStylesMapCache = createCache(() => {
    const { colorValuesMap } = this;
    return Object.keys(colorValuesMap).reduce((accum, hexColor) => {
      accum[hexColor] = htmlSafe(`
        background-color: #${hexColor};
      `);
      return accum;
    }, {});
  });

  get childStylesMap() {
    return getValue(this.#childStylesMapCache);
  }

  get secondaryChildStylesMap() {
    const { secondaryBackgroundColors } = this;
    return Object.keys(this.colorValuesMap).reduce((accum, hexColor) => {
      accum[hexColor] = htmlSafe(`
        color: #${hexColor};
        background-color: ${secondaryBackgroundColors[hexColor]};
      `);
      return accum;
    }, {});
  }
}

export default ChartLegendComponent;
